import { format } from 'date-fns';
import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { MONTH_DAY_YEAR_TIME_FORMAT, parseAirDateToISO } from '@air/utilities';
import { SOURCE_MAP } from '@air/utils-clips';

import { useCollapsibleSectionsContext } from '~/components/AssetModalV2/InfoPanel/CollapsibleSectionsContextProvider';
import { CollapsibleSection } from '~/components/AssetModalV2/InfoPanel/components/CollapsibleSection';
import { KeyValueInfoRow } from '~/components/AssetModalV2/InfoPanel/components/KeyValueInfoRow';
import {
  centralizedClipCreatedDateSelector,
  centralizedClipOwnerSelector,
  centralizedClipRecordedAtSelector,
  centralizedClipSourceSelector,
  centralizedClipUpdatedAtSelector,
} from '~/store/centralizedClip/selectors';

const formatDateSafely = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  const parsedDate = parseAirDateToISO(dateString);
  // Check if the parsed date is valid
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }
  return format(parsedDate, MONTH_DAY_YEAR_TIME_FORMAT);
};

export const FileInfoCollapsibleSection = memo(() => {
  const { expandedSections, toggleSection } = useCollapsibleSectionsContext();
  const recordedAt = useSelector(centralizedClipRecordedAtSelector);
  const isExpanded = expandedSections.fileInfo;
  const onOpenChange = useCallback(() => {
    toggleSection('fileInfo');
  }, [toggleSection]);

  const formattedCreatedAt = formatDateSafely(recordedAt);
  const updatedAt = useSelector(centralizedClipUpdatedAtSelector);
  const formattedUpdatedAt = formatDateSafely(updatedAt);
  const uploadedAt = useSelector(centralizedClipCreatedDateSelector);
  const formattedUploadedAt = formatDateSafely(uploadedAt);
  const owner = useSelector(centralizedClipOwnerSelector);
  const source = useSelector(centralizedClipSourceSelector);

  const hasFileInfo =
    !!formattedCreatedAt ||
    !!formattedUpdatedAt ||
    !!formattedUploadedAt ||
    (!!owner && owner.ownerName) ||
    !!formattedCreatedAt ||
    !!source;

  if (!hasFileInfo) return null;

  return (
    <CollapsibleSection
      title="File info"
      isExpanded={isExpanded}
      onOpenChange={onOpenChange}
      data-testid="FILE_INFO_SECTION"
    >
      <div className="flex flex-col gap-2">
        {!!formattedCreatedAt && (
          <KeyValueInfoRow label="Date Created" data-testid="FILE_INFO_DATE_CREATED" value={formattedCreatedAt} />
        )}
        {!!formattedUpdatedAt && (
          <KeyValueInfoRow label="Modified" data-testid="FILE_INFO_MODIFIED" value={formattedUpdatedAt} />
        )}
        {!!formattedUploadedAt && (
          <KeyValueInfoRow label="Uploaded" data-testid="FILE_INFO_UPLOADED" value={formattedUploadedAt} />
        )}
        {!!owner && <KeyValueInfoRow label="Uploaded by" data-testid="FILE_INFO_UPLOADED_BY" value={owner.ownerName} />}
        {!!source && (
          <KeyValueInfoRow label="Upload Source" data-testid="FILE_INFO_SOURCE" value={SOURCE_MAP[source]} />
        )}
      </div>
    </CollapsibleSection>
  );
});

FileInfoCollapsibleSection.displayName = 'FileInfoCollapsibleSection';
